import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { css } from '@styled-system/css'
import { Box, Flex, P } from 'ui/bend'
import { Checkbox, Input } from 'ui/bend/blocks/FormElements'
import { defaultValues as pStyles } from 'ui/bend/typography/P'
import { BOOLEAN_TYPES, TEXT_TYPES } from './buildRegistrationAnswers'
import Markdown from 'markdown-to-jsx'
import { Description } from 'components_v1/EventsView/EventDetailView.style'

const Form = styled(Box)`
  width: 100%;
  .check-box {
    width: 100%;
    height: 100%;
    .label {
      white-space: normal;
      overflow-wrap: break-word;
      word-break: break-word;
      color: black;
      padding: 0;
    }
  }
  .event-terms {
    text-decoration: underline;
  }
`

const Field = styled(Box)(
  css({
    mb: '20px'
  })
)

const FieldLabel = styled(P)(
  css({
    m: 0,
    mb: '8px',
    color: 'primary',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '150%'
  })
)

const RequiredMark = styled('span')(
  css({
    ml: '2px',
    color: 'errorDefault'
  })
)

const TermsCopy = styled(Box)(
  css({
    mb: '16px',
    p: '16px',
    maxHeight: '240px',
    overflowY: 'auto',
    backgroundColor: 'grey1',
    border: '1px solid',
    borderColor: 'grey3'
  })
)

const TermsCopyText = styled(Description)`
  font-size: 14 !important;
  line-height: 165%;
  white-space: pre-wrap;
  margin: 0;
`

// Match the design-system Input (border, padding, radius, type scale) so the
// dropdown doesn't read as a different control language than the text fields.
const NativeSelect = styled('select')(
  css({
    ...pStyles,
    width: '100%',
    py: '13px',
    px: '16px',
    color: 'primary',
    backgroundColor: 'base',
    border: '1px solid',
    borderColor: 'grey4',
    borderRadius: 0,
    '&:focus': {
      borderColor: 'primary',
      outline: 0
    }
  })
)

const MultiSelectOption = ({ option, checked, onChange }) => (
  <Box mb='8px'>
    <Checkbox
      name={option}
      label={option}
      value={checked}
      onChange={(event) => onChange(event.target.checked)}
    />
  </Box>
)

MultiSelectOption.propTypes = {
  option: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired
}

const EventRegistrationForm = ({
  questions = [],
  answers,
  errors = {},
  onAnswerChange
}) => {
  if (!questions.length) return null

  return (
    <Form>
      {questions.map((question) => {
        const error = errors[question.id]
        const value = answers[question.id]
        const isTerms = question.questionType === 'terms'
        const questionLabel =
          isTerms && question.terms?.url ? (
            <>
              By registering, I agree to the{' '}
              <a
                target='_blank'
                className='event-terms'
                href={question.terms?.url}
                rel='noreferrer'
              >
                event terms
              </a>
              .*
            </>
          ) : (
            `${question?.label}${question.required ? ' *' : ''}`
          )

        return (
          <Field key={question.id}>
            {!BOOLEAN_TYPES.includes(question.questionType) && (
              <FieldLabel>
                {questionLabel}
                {question.required && <RequiredMark>*</RequiredMark>}
              </FieldLabel>
            )}

            {isTerms && question.terms?.contentMd && (
              <TermsCopy>
                <TermsCopyText className='markdown'>
                  <Markdown
                    options={{
                      overrides: {
                        a: {
                          props: {
                            target: '_blank',
                            rel: 'noopener noreferrer'
                          }
                        }
                      }
                    }}
                  >
                    {question.terms?.contentMd}
                  </Markdown>
                </TermsCopyText>
              </TermsCopy>
            )}

            {BOOLEAN_TYPES.includes(question.questionType) && (
              <Checkbox
                containerProps={{ className: 'check-box' }}
                name={question.id}
                label={questionLabel}
                value={Boolean(value)}
                onChange={(event) =>
                  onAnswerChange(question.id, event.target.checked)
                }
              />
            )}

            {TEXT_TYPES.includes(question.questionType) && (
              <Input
                id={question.id}
                value={value || ''}
                containerProps={{ mt: 0 }}
                onChange={(event) =>
                  onAnswerChange(question.id, event.target.value)
                }
              />
            )}

            {question.questionType === 'dropdown' && (
              <NativeSelect
                value={value || ''}
                onChange={(event) =>
                  onAnswerChange(question.id, event.target.value)
                }
              >
                <option value=''>Select an option</option>
                {(question.options || []).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </NativeSelect>
            )}

            {question.questionType === 'multi-select' && (
              <Flex flexDirection='column' gap='8px'>
                {(question.options || []).map((option) => {
                  const selected = Array.isArray(value) ? value : []
                  return (
                    <MultiSelectOption
                      key={option}
                      option={option}
                      checked={selected.includes(option)}
                      onChange={(checked) => {
                        const next = checked
                          ? [...selected, option]
                          : selected.filter((item) => item !== option)
                        onAnswerChange(question.id, next)
                      }}
                    />
                  )
                })}
              </Flex>
            )}

            {question.questionType === 'company' && (
              <Flex flexDirection='column' gap='12px'>
                <Input
                  id='company_data'
                  label='Company'
                  value={value?.company || ''}
                  onChange={(event) =>
                    onAnswerChange(question.id, {
                      ...(value || {}),
                      company: event.target.value
                    })
                  }
                />
                {question.collectJobTitle !== false && (
                  <Input
                    id='job_title'
                    label='Job title'
                    value={value?.jobTitle || ''}
                    onChange={(event) =>
                      onAnswerChange(question.id, {
                        ...(value || {}),
                        jobTitle: event.target.value
                      })
                    }
                  />
                )}
              </Flex>
            )}

            {error && (
              <P color='errorDefault' fontSize='13px' mt='8px' mb={0}>
                {error}
              </P>
            )}
          </Field>
        )
      })}
    </Form>
  )
}

EventRegistrationForm.propTypes = {
  questions: PropTypes.arrayOf(PropTypes.object),
  answers: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onAnswerChange: PropTypes.func.isRequired
}

export default EventRegistrationForm
